import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Target, Zap, RefreshCw } from 'lucide-react';
import HubLayout from '../../components/hub/HubLayout.jsx';
import ExamRunner from '../../components/hub/ExamRunner.jsx';
import { AI } from '../../lib/ai.js';
import {
  computeWeakDomains,
  pickTargetDomains,
  samplePracticeAnchors,
  buildPrompt,
  parseDrillJSON,
  statusLabel,
} from '../../lib/drillGen.js';

const DRILL_ID = '__drill__';
const DRILL_COUNT = 10;
const DRILL_MINUTES = 15;

export default function ObjectiveTests({ config }) {
  const HISTORY_KEY = `${config.storagePrefix}.ot.history`;
  const DRILL_BANK_KEY = `${config.storagePrefix}.ai.drill.last`;
  const DRILL_STATE_KEY = `${config.storagePrefix}.ai.drill.state`;
  const loadHistory = () => {
    try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '{}'); } catch { return {}; }
  };

  const tests = config.data.objective; // { D1: { id, title, weight, questions: [...] }, ... }
  const [activeId, setActiveId] = useState(null);
  const [history, setHistory] = useState(loadHistory);

  // Drill state
  const [drill, setDrill] = useState(() => {
    try {
      const raw = localStorage.getItem(`${config.storagePrefix}.ai.drill.last`);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [drillStatus, setDrillStatus] = useState('idle'); // idle | generating | error
  const [drillError, setDrillError] = useState('');
  const [drillProgress, setDrillProgress] = useState(0);
  const drillAbortRef = useRef(false);

  useEffect(() => {
    if (!activeId) setHistory(loadHistory());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  useEffect(() => {
    if (!activeId) return undefined;
    const stateKey = `${config.storagePrefix}.ot.${activeId}`;
    const handler = () => {
      try {
        const s = JSON.parse(localStorage.getItem(stateKey) || 'null');
        if (s && s.finished) {
          const test = tests[activeId];
          let correct = 0;
          test.questions.forEach((q) => { if (s.answers[q.id] === q.correct) correct++; });
          const total = test.questions.length;
          const pct = Math.round((correct / total) * 100);
          const h = loadHistory();
          const cur = h[activeId] || { best: 0, lastPct: 0, attempts: 0 };
          const next = {
            best: Math.max(cur.best || 0, pct),
            lastPct: pct,
            attempts: (cur.attempts || 0) + 1,
          };
          if (cur.lastPct !== pct || cur.attempts !== next.attempts) {
            h[activeId] = next;
            localStorage.setItem(HISTORY_KEY, JSON.stringify(h));
            setHistory(h);
          }
        }
      } catch { /* ignore */ }
    };
    const interval = setInterval(handler, 800);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, tests]);

  function clearAll() {
    if (!confirm('Clear all objective-test history and saved attempts?')) return;
    try {
      Object.keys(tests).forEach((id) => localStorage.removeItem(`${config.storagePrefix}.ot.${id}`));
      localStorage.removeItem(HISTORY_KEY);
    } catch { /* ignore */ }
    setHistory({});
  }

  const objMinutes = Math.round(config.objective.totalSeconds / 60);

  // ── Drill: compute weak domains ───────────────────────────────────
  const weakList = useMemo(() => computeWeakDomains(config, history), [config, history]);
  const targetDomains = useMemo(() => pickTargetDomains(weakList, 2), [weakList]);
  const targetDetail = targetDomains.map((d) => weakList.find((w) => w.domain === d)).filter(Boolean);
  const aiReady = AI.isConfigured?.() ?? false;

  async function generateDrill() {
    if (!aiReady) return;
    if (!targetDomains.length) return;
    setDrillStatus('generating');
    setDrillError('');
    setDrillProgress(0);
    drillAbortRef.current = false;
    // Clear stale state if regenerating
    try { localStorage.removeItem(DRILL_STATE_KEY); } catch { /* ignore */ }

    const anchors = samplePracticeAnchors(config, targetDomains, 2);
    const { system, prompt } = buildPrompt(config, targetDomains, anchors, DRILL_COUNT);

    let latest = '';
    let parsed = null;
    let lastErr = '';

    const tryOnce = (extraSystem = '') =>
      new Promise((resolve) => {
        latest = '';
        AI.streamInto(
          (full) => {
            if (drillAbortRef.current) return;
            latest = full;
            // Cheap progress heuristic: count "{" appearances for each question
            const seen = (full.match(/"q"\s*:/g) || []).length;
            setDrillProgress(Math.min(100, Math.round((seen / DRILL_COUNT) * 100)));
          },
          {
            system: system + extraSystem,
            prompt,
            onDone: () => {
              if (drillAbortRef.current) return resolve();
              try {
                parsed = parseDrillJSON(latest, targetDomains);
                resolve();
              } catch (e) {
                lastErr = e.message || String(e);
                resolve();
              }
            },
            onError: (msg) => {
              if (drillAbortRef.current) return resolve();
              lastErr = msg || 'AI request failed.';
              resolve();
            },
          }
        );
      });

    await tryOnce();
    if (!parsed && !drillAbortRef.current) {
      // One retry with stricter instruction
      await tryOnce('\n\nYour previous output was malformed. Output STRICT JSON only — no prose, no markdown, no code fences.');
    }

    if (drillAbortRef.current) {
      setDrillStatus('idle');
      return;
    }

    if (!parsed) {
      setDrillStatus('error');
      setDrillError(lastErr || 'Could not generate a valid drill. Try again.');
      return;
    }

    const blob = {
      ts: Date.now(),
      targetDomains,
      questions: parsed,
    };
    try { localStorage.setItem(DRILL_BANK_KEY, JSON.stringify(blob)); } catch { /* ignore */ }
    setDrill(blob);
    setDrillStatus('idle');
    setDrillProgress(100);
    setActiveId(DRILL_ID);
  }

  function cancelDrill() {
    drillAbortRef.current = true;
    setDrillStatus('idle');
    setDrillProgress(0);
  }

  // ── Drill: runner branch ──────────────────────────────────────────
  if (activeId === DRILL_ID && drill) {
    const drillLabel = drill.targetDomains.join(' · ');
    return (
      <HubLayout config={config}>
        <ExamRunner
          questions={drill.questions}
          domainNames={config.domainNames}
          totalSeconds={DRILL_MINUTES * 60}
          passPct={config.objective.passPct}
          stateKey={DRILL_STATE_KEY}
          briefingItems={[
            { label: 'QUESTIONS', value: drill.questions.length },
            { label: 'TIME LIMIT', value: `${DRILL_MINUTES} MIN` },
            { label: 'PASSING SCORE', value: `${config.objective.passPct}%` },
            { label: 'SOURCE', value: 'AI' },
          ]}
          startTitle={`Targeted Drill — ${drillLabel}`}
          startTag={`AI-generated · ${drill.questions.length} Q`}
          rules={[
            '<strong>AI-GENERATED</strong> These questions were generated on-the-fly to target your weakest domains. Treat them as drills, not as official exam content.',
            '<strong>SHUFFLED EACH ATTEMPT</strong> Question order and answer-option order are randomized.',
            '<strong>PERSISTENT MID-ATTEMPT</strong> Closing the tab pauses your attempt; reopening resumes it.',
            `<strong>${DRILL_MINUTES}-MINUTE TIMER</strong> Roughly 1.5 minutes per question. Auto-submits at zero.`,
          ]}
          onPickAnother={() => setActiveId(null)}
          pickAnotherLabel="← Back to domains"
          examName={`${config.title}${config.code ? ` (${config.code})` : ''} — Targeted Drill`}
        />
      </HubLayout>
    );
  }

  if (activeId && activeId !== DRILL_ID) {
    const test = tests[activeId];
    return (
      <HubLayout config={config}>
        <ExamRunner
          questions={test.questions}
          domainNames={config.domainNames}
          totalSeconds={config.objective.totalSeconds}
          passPct={config.objective.passPct}
          stateKey={`${config.storagePrefix}.ot.${activeId}`}
          briefingItems={[
            { label: 'QUESTIONS', value: test.questions.length },
            { label: 'TIME LIMIT', value: `${objMinutes} MIN` },
            { label: 'PASSING SCORE', value: `${config.objective.passPct}%` },
            { label: 'DOMAIN', value: activeId },
          ]}
          startTitle={test.title}
          startTag={`${activeId} · ${test.weight}`}
          rules={[
            '<strong>SHUFFLED EVERY ATTEMPT</strong> Question order and answer-option order are randomized on each new attempt — no order-memorization.',
            '<strong>PERSISTENT MID-ATTEMPT</strong> If you close the tab, the exact attempt resumes where you left off.',
            `<strong>${objMinutes}-MINUTE TIMER</strong> Roughly one minute per question. Auto-submits when time expires.`,
            '<strong>FULL REVIEW</strong> After submission, every question is shown with the correct answer, your selection, and a written explanation.',
          ]}
          onPickAnother={() => setActiveId(null)}
          pickAnotherLabel="← Back to domains"
          examName={`${config.title}${config.code ? ` (${config.code})` : ''} — ${activeId}`}
        />
      </HubLayout>
    );
  }

  return (
    <HubLayout config={config}>
      <section className="drill-section">
        <div className="section-head">
          <h2>Targeted Drill <span className="drill-ai-chip"><Zap size={11} strokeWidth={2.5} /> AI</span></h2>
          <span className="section-tag">10 Q · {DRILL_MINUTES} min · weakest domains</span>
        </div>

        {!aiReady ? (
          <div className="callout drill-callout">
            <strong>AI not configured</strong>
            Add an API key in <Link to="/ai-settings">AI Settings</Link> to unlock weakness-aware drills.
            Drills generate fresh questions targeting your two weakest objective-test domains.
          </div>
        ) : !targetDomains.length ? (
          <div className="callout drill-callout">
            <strong>Take at least one sectional test first</strong>
            Drills target your weakest domains — complete a sectional below and the AI will start generating
            tailored 10-question drills aimed at where you need it most.
          </div>
        ) : (
          <div className={`drill-card drill-card--${drillStatus}`}>
            <div className="drill-card__head">
              <div className="drill-card__icon"><Target size={18} strokeWidth={2.5} /></div>
              <div className="drill-card__title">
                <div className="drill-card__label">WEAK DOMAINS DETECTED</div>
                <div className="drill-card__list">
                  {targetDetail.map((d) => (
                    <span key={d.domain} className={`drill-card__weak drill-card__weak--${d.status}`}>
                      <strong>{d.domain}</strong>
                      <span className="drill-card__status">{statusLabel(d.status)}</span>
                      <span className="drill-card__score">
                        {d.attempts ? `best ${d.best}%` : 'no attempts'}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {drillStatus === 'generating' ? (
              <div className="drill-card__stream">
                <div className="drill-card__stream-bar">
                  <span style={{ width: `${drillProgress}%` }} />
                </div>
                <div className="drill-card__stream-row">
                  <span>GENERATING {drillProgress}% — drafting {DRILL_COUNT} questions</span>
                  <button type="button" className="btn-mini" onClick={cancelDrill}>Cancel</button>
                </div>
              </div>
            ) : drillStatus === 'error' ? (
              <div className="drill-card__err">
                <strong>Generation failed</strong>
                <span>{drillError}</span>
                <button type="button" className="drill-card__btn" onClick={generateDrill}>
                  <RefreshCw size={13} strokeWidth={2.5} /> Try again
                </button>
              </div>
            ) : (
              <div className="drill-card__actions">
                <button type="button" className="drill-card__btn" onClick={generateDrill}>
                  <Zap size={14} strokeWidth={2.5} />
                  {drill ? 'Regenerate drill' : `Generate ${DRILL_COUNT} questions`}
                </button>
                {drill && (
                  <button
                    type="button"
                    className="drill-card__btn drill-card__btn--ghost"
                    onClick={() => setActiveId(DRILL_ID)}
                  >
                    Resume last drill →
                  </button>
                )}
                {drill && (
                  <span className="drill-card__meta">
                    Last drill · {new Date(drill.ts).toLocaleString()} · {drill.targetDomains.join(', ')}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      <section>
        <div className="section-head">
          <h2>Pick a Domain</h2>
          <span className="section-tag">{Object.keys(tests).length} sectional tests · {objMinutes} min each</span>
        </div>
        <p>
          Each test draws vendor-neutral questions from the corresponding {config.code} domain. Questions and answer
          options are <strong>shuffled fresh every attempt</strong>. Pass threshold is {config.objective.passPct}% — same
          as the main exam. Use these to drill specific weak areas before the full practice exam.
        </p>

        <div className="callout">
          <strong>Tactic</strong>
          Take the full practice exam first to identify weak domains, then drill those domains here. Repeat until you
          can pass each objective test consistently above 90%.
        </div>

        <div className="qlink-grid">
          {Object.values(tests).map((t) => {
            const domainKey = Object.keys(tests).find((k) => tests[k] === t);
            const h = history[domainKey] || { best: 0, lastPct: 0, attempts: 0 };
            return (
              <a
                key={domainKey}
                href="#"
                className="qlink"
                onClick={(e) => { e.preventDefault(); setActiveId(domainKey); }}
              >
                <div className="qlink-id">{domainKey} · {t.weight}</div>
                <h4>{t.title}</h4>
                <span className="desc">
                  {t.questions.length} Q · {h.attempts} attempt{h.attempts === 1 ? '' : 's'}
                  {h.attempts > 0 && ` · best ${h.best}% · last ${h.lastPct}%`}
                </span>
                <div className="deck-mini-bar"><span style={{ width: `${h.best || 0}%` }}></span></div>
                <span className="arrow">→</span>
              </a>
            );
          })}
        </div>

        <div className="hero-cta" style={{ marginTop: '1.5rem' }}>
          <button type="button" className="btn-mini" onClick={clearAll}>Clear all objective-test history</button>
        </div>
      </section>
    </HubLayout>
  );
}
